import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { UserRole } from '@bst-stellar/shared';

const SIDEBAR_COLLAPSED_KEY = 'employee-sidebar-collapsed';

export default function EmployeeLayout() {
    const { user, logout } = useAuth();
    const { tenant } = useTenant();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    }, [isCollapsed]);

    const isAdmin = user?.role === UserRole.COMPANY_ADMIN || user?.role === UserRole.DEPT_ADMIN;

    const navItems = [
        { path: '/', label: 'Dashboard', icon: '🏠' },
        { path: '/games', label: 'Games', icon: '🎮' },
        { path: '/claims', label: 'My Claims', icon: '📋' },
        { path: '/leaves', label: 'Leave Tracker', icon: '📅' },
        { path: '/profile', label: 'Profile', icon: '👤' },
    ];

    return (
        <div className="min-h-screen flex">
            {/* Sidebar */}
            <aside className={`${isCollapsed ? 'w-16' : 'w-64'} bg-surface shadow-lg border-r border-border flex flex-col transition-all duration-300`}>
                {/* Company Header */}
                <div className="p-4 border-b border-border">
                    <div className="flex items-center gap-3">
                        {tenant?.logoUrl && (
                            <img
                                src={tenant.logoUrl}
                                alt={tenant.name}
                                className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                                title={isCollapsed ? tenant.name : undefined}
                            />
                        )}
                        {!isCollapsed && (
                            <div className="overflow-hidden">
                                <h1 className="font-bold text-text-primary whitespace-nowrap">{tenant?.name || 'Stellar'}</h1>
                                <p className="text-xs text-text-muted whitespace-nowrap">Employee Portal</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-3">
                    <div className="space-y-1">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path ||
                                (item.path !== '/' && location.pathname.startsWith(item.path));
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    title={isCollapsed ? item.label : undefined}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${isActive
                                        ? 'bg-primary text-white hover:bg-primary hover:text-white shadow-md cursor-default'
                                        : 'text-text-secondary hover:bg-background hover:text-text-primary'
                                        } ${isCollapsed ? 'justify-center' : ''}`}
                                >
                                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                                    {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.label}</span>}
                                </Link>
                            );
                        })}
                    </div>

                    {/* Admin Link (if admin) */}
                    {isAdmin && (
                        <div className="mt-6 pt-4 border-t border-border">
                            <Link
                                to="/admin"
                                title={isCollapsed ? 'Admin Console' : undefined}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-secondary hover:bg-background hover:text-text-primary transition-all duration-200 ${isCollapsed ? 'justify-center' : ''}`}
                            >
                                <span className="text-lg flex-shrink-0">⚙️</span>
                                {!isCollapsed && <span className="font-medium whitespace-nowrap">Admin Console</span>}
                            </Link>
                        </div>
                    )}
                </nav>

                {/* Collapse Toggle */}
                <div className="px-2 py-3 border-t border-border">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm text-text-secondary hover:bg-background hover:text-text-primary rounded-lg transition-all duration-200 group"
                    >
                        <svg
                            className={`w-5 h-5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                        {!isCollapsed && <span className="font-medium">Collapse</span>}
                    </button>
                </div>

                {/* User Section */}
                <div className={`p-4 border-t border-border ${isCollapsed ? 'px-2' : ''}`}>
                    <div className={`flex items-center gap-3 mb-3 ${isCollapsed ? 'justify-center' : ''}`}>
                        <div
                            className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-bold flex-shrink-0"
                            title={isCollapsed ? (user?.name || 'User') : undefined}
                        >
                            {user?.name?.[0] || 'U'}
                        </div>
                        {!isCollapsed && (
                            <div className="flex-1 min-w-0 overflow-hidden">
                                <p className="font-medium text-text-primary truncate">{user?.name}</p>
                                <p className="text-xs text-text-muted truncate">{user?.email}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={logout}
                        title={isCollapsed ? 'Logout' : undefined}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors`}
                    >
                        <span>🚪</span>
                        {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-auto">
                <Outlet />
            </main>
        </div>
    );
}
